import { updateOrderNationalityListApi, useGetRegionList } from "@/api/services/global/master";
import { TGetNationalityListOutputDto } from "@/api/services/global/master/dtos/nationality/get-master-nationality-list.dto";
import { IconOrderTable } from "@/assets/icons/IconOrderTable";
import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Select } from "@/components/elements/Select";
import { Switch } from "@/components/elements/Switch";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { HiddenNationalityModal } from "@/features/master-data/components/Nationality/HiddenNationalityModal";
import { NationalityFormModal } from "@/features/master-data/components/Nationality/NationalityFormModal";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { background, blue, button, divider, green, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { Box, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { Link } from "react-router-dom";

const stickySwitchColumnSx = {
  minWidth: "120px",
  position: "sticky",
  right: "100px",
  bgcolor: background.white,
  borderLeft: `1px solid ${divider.middle}`,
} as const;

const stickyEditColumnSx = {
  minWidth: "100px",
  position: "sticky",
  right: 0,
  bgcolor: background.white,
} as const;

export const MasterDataNationality = () => {
  const nationalityFormModal = useVisible();

  const queryClient = useQueryClient();

  const [selectedRegionId, setSelectedRegionId] = useState<string | undefined>("1"); //id: アジア
  const [enableSaveOrderBtn, setEnableOrderBtn] = useState<boolean>(false);

  const [hiddenNationality, setHiddenNationality] = useState<TGetNationalityListOutputDto>();

  const [selectedNationality, setSelectedNationality] = useState<TGetNationalityListOutputDto>();

  const { data, isLoading, regionOptions } = useGetRegionList();

  const [rows, setRows] = useState<TGetNationalityListOutputDto[]>([]);

  useEffect(() => {
    if (data && data.length > 0) {
      if (selectedRegionId) {
        setRows(data.find((item) => toNumber(item.id) === toNumber(selectedRegionId))?.nationalities ?? []);
      } else {
        setRows(data.flatMap((item) => (item ? item.nationalities : [])));
      }
    }
  }, [data, selectedRegionId]);

  const handleSaveOrder = async () => {
    try {
      const rs = await updateOrderNationalityListApi({
        nationalityIds: rows.map((item) => toNumber(item.id)),
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_REGION] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setEnableOrderBtn(false);
    } catch (error) {
      showError(error);
    }
  };

  // Handle row dragging
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !result.source || result.source.index === result.destination.index) return;
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    if (
      (result.destination.index > 0 && removed.region.regionName !== reorderedRows[result.destination.index - 1].region.regionName) ||
      (result.destination.index === 0 && removed.region.regionName !== reorderedRows[result.destination.index + 1].region.regionName)
    ) {
      toast.error("他の地域にドラッグしないでください。");
      return;
    }
    setRows(reorderedRows);
    setEnableOrderBtn(true);
  };

  if (isLoading || rows.length === 0) return <LoadingPage />;

  return (
    <>
      <TopPage title={"「" + MASTER_DATA_TYPE_TITLE.NATIONALITY + "」の編集"} enablePageBack={false} />
      <InnerBox>
        <Stack direction="row" justifyContent="space-between" mb={3}>
          <Select
            placeholder="地域を検索する"
            sx={{ width: 200 }}
            options={regionOptions.map((rg) => ({ ...rg, hidden: false }))}
            loading={isLoading}
            value={selectedRegionId}
            onChange={(e) => setSelectedRegionId(e.target.value as string)}
            onClickCloseIcon={() => setSelectedRegionId(undefined)}
          />
          <Stack direction="row" justifyContent="flex-end" gap={3}>
            <Button size="sm" variant="primary" onClick={() => nationalityFormModal.open()}>
              項目追加
            </Button>
          </Stack>
        </Stack>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="tableRows" direction="vertical">
            {(provided) => (
              <StyledTable {...provided.droppableProps} ref={provided.innerRef}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>地域</TableCell>
                      <TableCell>名称</TableCell>
                      <TableCell>名称（英語）</TableCell>
                      <TableCell>期間更新CSVのコード</TableCell>
                      <TableCell>随時届出CSVのコード</TableCell>
                      <TableCell>最終更新日</TableCell>
                      <TableCell sx={{ ...stickySwitchColumnSx, textAlign: "center !important" }}>有効／無効</TableCell>
                      <TableCell sx={{ ...stickyEditColumnSx, textAlign: "center !important" }}>編集</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                        {(provided) => (
                          <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                            <TableCell sx={{ minWidth: "40px" }} {...provided.dragHandleProps}>
                              <IconOrderTable color={blue[80]} />
                            </TableCell>
                            <TableCell sx={{ minWidth: "120px" }}>
                              <Typography component="p" color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.region.regionName || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: "200px" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.nationalityName || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: "220px" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.engNationalityName || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: "190px" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.nationalRenewVisaCsvCode || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: "190px" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.nationalityCode || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: "160px" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} sx={{ px: 0.5 }} variant="body14">
                                {row.updatedAt ? dayjs(row.updatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ ...stickySwitchColumnSx, textAlign: "center !important" }}>
                              <Switch
                                sx={{
                                  marginRight: 0,
                                  marginLeft: 0,
                                }}
                                checked={!row.isHidden}
                                onChange={() => setHiddenNationality(row)}
                                color="primary"
                              />
                            </TableCell>
                            <TableCell sx={{ ...stickyEditColumnSx, textAlign: "center !important" }}>
                              <Button
                                size="sm"
                                onClick={() => {
                                  nationalityFormModal.open();
                                  setSelectedNationality(row);
                                }}
                              >
                                編集
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </StyledTable>
            )}
          </Droppable>
        </DragDropContext>
      </InnerBox>
      <FooterActionBar mt={4}>
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Button component={Link} to={AppRoutes.masterData} variant="ghost" size="sm">
            戻る
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleSaveOrder()} disabled={!enableSaveOrderBtn}>
            オーダーを保存する
          </Button>
        </Stack>
      </FooterActionBar>
      {nationalityFormModal.visible && (
        <NationalityFormModal
          onClose={() => {
            nationalityFormModal.close();
            setSelectedNationality(undefined);
          }}
          isOpen={nationalityFormModal.visible}
          selectedNationality={selectedNationality}
          setRows={setRows}
        />
      )}

      <HiddenNationalityModal hiddenNationality={hiddenNationality} setHiddenNationality={setHiddenNationality} />
      <ConfirmLeavePage shouldBlock={enableSaveOrderBtn}></ConfirmLeavePage>
    </>
  );
};

const StyledTable = styled(Box)`
  border-radius: ${rounded.sm};
  overflow: auto;
  max-height: calc(100vh - 380px);
  border: 1px solid ${divider.middle};
  background-color: ${background.white};
  * {
    box-shadow: none;
  }
  .MuiToolbar-dense {
    display: none;
  }
  table {
    border-collapse: separate;
    border-spacing: 0;
  }
  .MuiTableCell-head {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    position: sticky;
    background-color: white;
    z-index: 500;
    top: 0;
  }
  .Mui-TableHeadCell-Content-Labels {
    > span {
      margin-left: 4px;
      img {
        opacity: 0.2;
      }
      .MuiBadge-badge {
        display: none;
      }
    }
  }
  .MuiTableCell-body {
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }

  // Default
  .MuiTableRow-root {
    background-color: ${background.white};
    box-shadow: none;
    cursor: initial;
    .MuiTableCell-root {
      padding: ${spacing.xxs} ${spacing.sm};
      border-color: ${divider.middle};
      opacity: 1;
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  // Hover
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }
  // Click/Pressed
  .Mui-selected {
    background-color: ${background.white} !important;
    td::after {
      background: ${background.secondary}!important;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  .MuiCheckbox-root {
    color: ${button.primary}!important;
    height: 24px;
    width: 24px;
    padding: 0;
  }

  .MuiTableCell-root {
    padding: 4px 8px !important;
  }

  // Disabled Row
  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  // added row
  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.failed {
    background: ${background.error}!important;
    &::after {
      background: unset !important;
    }
  }

  // Clickable row
  .MuiTableCell-root.clickable {
    cursor: pointer;
  }
`;
